import '../auth.scss';

import { ReactComponent as Logo } from '@basqet-dashboard/common/assets/logo.svg';
import { Button, FormInput } from '@basqet-dashboard/common/components';
import { APP_NAME } from '@basqet-dashboard/common/config';
import { UserContext } from '@basqet-dashboard/common/context';
import { useDocumentTitle, useSessionStorage } from '@basqet-dashboard/common/hooks';
import { useLoginMutation } from '@basqet-dashboard/common/hooks/mutations/auth';
import { FLAGS, loginAuthUserSchema } from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

function Login() {
  const merchantOidcEnabled = useFeatureFlagEnabled(FLAGS.MERCHANT_OIDC) === true;
  return merchantOidcEnabled ? <OidcLogin /> : <LegacyLogin />;
}

function OidcLogin() {
  const auth = useAuth();
  useDocumentTitle('Login');

  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading) {
      auth.signinRedirect({
        extraQueryParams: { legacy_source: 'basqet' }
      });
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.signinRedirect]);

  return (
    <main className="auth">
      <div className="auth__content">
        <h1 className="page__title">
          Redirecting to <span className="text-cursive">login</span>...
        </h1>
        <div className="auth__card" style={{ textAlign: 'center', padding: '40px' }}>
          <Logo />
          <p style={{ marginTop: '20px' }}>
            Please wait while we redirect you to our identity provider.
          </p>
          <Button
            style={{ marginTop: '20px' }}
            onClick={() => auth.signinRedirect({ extraQueryParams: { legacy_source: 'basqet' } })}
            isLoading={auth.isLoading}>
            Click here if not redirected
          </Button>
        </div>
      </div>
    </main>
  );
}

function LegacyLogin() {
  const { setUser } = useContext(UserContext);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm({ mode: 'onChange', resolver: yupResolver(loginAuthUserSchema) });
  useDocumentTitle('Login');

  const captchaRef = useRef(null);
  const [captchaValue, setCaptchaValue] = useState('l');
  const [, setToken] = useSessionStorage(APP_NAME, '');

  const { mutateAsync: loginAction, isLoading: isLoginLoading } = useLoginMutation();

  const onSubmit = async (body) => {
    await loginAction(
      { ...body, recaptcha: captchaValue },
      {
        onSuccess: (data) => {
          const {
            data: { accessToken, merchantUser },
            status
          } = data;

          if (status !== 'success') return;

          if (merchantUser && !merchantUser.emailVerified) {
            sessionStorage.removeItem(APP_NAME);
            window.location.replace(
              `/signup?verify_email=${encodeURIComponent(merchantUser.email)}`
            );
            return;
          }

          setToken({ token: accessToken, merchantUser });

          if (merchantUser) {
            setUser(merchantUser);
            window.location.replace('/');
            return;
          }

          window.location.replace('/secure');
        },
        onError: (error) => {
          if (error.response) {
            const { data } = error.response;
            const message =
              data ===
              'You have exceeded the allowed number of requests, please try again in the next 5 minutes'
                ? data
                : data.message;
            toast.error(message);
            if (data && data.email) {
              setError('email', {
                type: 'manual',
                ...data.email
              });
            }
            return;
          }

          return toast.error(error.message);
        },
        onSettled: () => {
          captchaRef?.current?.reset();
          setCaptchaValue('l');
        }
      }
    );
  };

  return (
    <main className="auth">
      <div className="auth__content">
        <h1 className="page__title">
          Welcome <span className="text-cursive">back</span>
        </h1>
        <div className="auth__card">
          <Logo />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="auth__card__input-area">
              <FormInput
                data-testid="login-email-input"
                label="Email"
                role="menu"
                placeholder="Enter your email"
                name="email"
                type="email"
                error={errors.email && errors.email.message}
                register={register('email')}
              />
              <FormInput
                data-testid="login-password-input"
                label="Password"
                placeholder="Enter your password"
                name="password"
                type="password"
                error={errors.password && errors.password.message}
                {...register('password')}
              />
            </div>
            <Button
              data-testid="login-submit-button"
              isFull
              type="submit"
              isLoading={isLoginLoading}>
              Log in
            </Button>
          </form>
          <p className="auth__link">
            Don&apos;t have an account? <Link to="/signup">Sign up</Link>
          </p>
          <p className="auth__link">
            <Link to="/forgot-password">Forgot password?</Link>
          </p>
        </div>
      </div>
    </main>
  );
}

export { Login };
